import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { LatLng, LeafletMouseEvent, Icon } from 'leaflet';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  ButtonGroup,
  Chip,
  Alert
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusWeak as CenterIcon,
  MyLocation as MyLocationIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Импорт стилей Leaflet
import 'leaflet/dist/leaflet.css';

import type { Coordinates, MapMarker, MapConfig } from '../../types';

// Исправление иконок маркеров Leaflet в React
delete (Icon.Default.prototype as any)._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Кастомные иконки для различных статусов устройств
const createCustomIcon = (color: string) => new Icon({
  iconUrl: `data:image/svg+xml;base64,${btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30">
      <circle cx="12" cy="12" r="8" fill="${color}" stroke="white" stroke-width="2"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
    </svg>
  `)}`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15],
});

const DEVICE_ICONS = {
  online: createCustomIcon('#4caf50'),
  offline: createCustomIcon('#9e9e9e'),
  error: createCustomIcon('#f44336'),
  default: createCustomIcon('#1976d2'),
};

interface RealMapComponentProps {
  markers?: MapMarker[];
  onMarkerClick?: (marker: MapMarker) => void;
  onMapClick?: (coordinates: Coordinates) => void;
  selectedMarker?: MapMarker | null;
  config?: Partial<MapConfig>;
  showControls?: boolean;
  interactive?: boolean;
  className?: string;
  height?: number | string;
}

const DEFAULT_CONFIG: MapConfig = {
  center: { latitude: 55.7558, longitude: 37.6176 }, // Москва по умолчанию
  zoom: 10,
};

// Компонент для обработки кликов по карте
const MapClickHandler: React.FC<{ onMapClick?: (coordinates: Coordinates) => void }> = ({ onMapClick }) => {
  useMapEvents({
    click: (e: LeafletMouseEvent) => {
      if (onMapClick) {
        onMapClick({
          latitude: e.latlng.lat,
          longitude: e.latlng.lng
        });
      }
    },
  });
  return null;
};

// Компонент для управления картой
const MapController: React.FC<{ 
  center: Coordinates; 
  zoom: number; 
  onZoomChange: (zoom: number) => void;
}> = ({ center, zoom, onZoomChange }) => {
  const map = useMap();

  useEffect(() => {
    map.setView([center.latitude, center.longitude], zoom);
  }, [map, center, zoom]);

  useEffect(() => {
    const handleZoomEnd = () => {
      onZoomChange(map.getZoom());
    };

    map.on('zoomend', handleZoomEnd);
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, onZoomChange]);

  return null;
};

export const RealMapComponent: React.FC<RealMapComponentProps> = ({
  markers = [],
  onMarkerClick,
  onMapClick,
  selectedMarker,
  config = {},
  showControls = true,
  interactive = true,
  className,
  height = 400,
}) => {
  const { t } = useTranslation();
  
  const [mapConfig, setMapConfig] = useState<MapConfig>({ ...DEFAULT_CONFIG, ...config });
  const [currentZoom, setCurrentZoom] = useState(mapConfig.zoom);

  // Обработка клика по маркеру
  const handleMarkerClick = useCallback((marker: MapMarker) => {
    if (onMarkerClick) {
      onMarkerClick(marker);
    }
  }, [onMarkerClick]);

  // Управление зумом
  const handleZoom = useCallback((delta: number) => {
    const newZoom = Math.max(1, Math.min(18, currentZoom + delta));
    setCurrentZoom(newZoom);
    setMapConfig(prev => ({ ...prev, zoom: newZoom }));
  }, [currentZoom]);

  // Центрирование карты на устройствах
  const handleCenter = useCallback(() => {
    if (markers.length === 0) return;

    let minLat = Infinity, maxLat = -Infinity;
    let minLng = Infinity, maxLng = -Infinity;

    markers.forEach(marker => {
      const { latitude, longitude } = marker.coordinates;
      minLat = Math.min(minLat, latitude);
      maxLat = Math.max(maxLat, latitude);
      minLng = Math.min(minLng, longitude);
      maxLng = Math.max(maxLng, longitude);
    });

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    setMapConfig(prev => ({
      ...prev,
      center: { latitude: centerLat, longitude: centerLng },
      zoom: 12
    }));
    setCurrentZoom(12);
  }, [markers]);

  // Получение текущего местоположения
  const handleGetCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newCenter = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setMapConfig(prev => ({ ...prev, center: newCenter, zoom: 15 }));
          setCurrentZoom(15);
        },
        (error) => {
          console.error('Не удалось получить текущее местоположение:', error);
        }
      );
    }
  }, []);

  return (
    <Box className={className} sx={{ position: 'relative', height, minHeight: 300 }}>
      {/* Панель управления */}
      {showControls && (
        <Paper 
          elevation={3} 
          sx={{ 
            position: 'absolute', 
            top: 16, 
            right: 16, 
            zIndex: 1000,
            bgcolor: 'rgba(50, 50, 50, 0.9)', // Темный фон для контраста
            color: 'white', // Белый текст на темном фоне
            backdropFilter: 'blur(8px)',
            '& .MuiIconButton-root': {
              color: 'white', // Белые иконки
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)' // Подсветка при наведении
              }
            }
          }}
        >
          <ButtonGroup orientation="vertical" size="small">
            <Tooltip title={t('map.zoomIn')}>
              <IconButton onClick={() => handleZoom(1)}>
                <ZoomInIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={t('map.zoomOut')}>
              <IconButton onClick={() => handleZoom(-1)}>
                <ZoomOutIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={t('map.centerView')}>
              <IconButton onClick={handleCenter} disabled={markers.length === 0}>
                <CenterIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('map.myLocation')}>
              <IconButton onClick={handleGetCurrentLocation}>
                <MyLocationIcon />
              </IconButton>
            </Tooltip>
          </ButtonGroup>
        </Paper>
      )}

      {/* Информационная панель */}
      <Paper 
        elevation={3}
        sx={{ 
          position: 'absolute', 
          bottom: 16, 
          left: 16, 
          zIndex: 1000,
          p: 2,
          bgcolor: 'rgba(50, 50, 50, 0.9)', // Темный фон
          color: 'white', // Белый текст
          backdropFilter: 'blur(8px)',
          borderRadius: 2
        }}
      >
        <Typography variant="caption" display="block" sx={{ color: 'white', fontWeight: 500 }}>
          {t('map.zoom')}: {currentZoom}
        </Typography>
        <Typography variant="caption" display="block" sx={{ color: 'white', fontWeight: 500 }}>
          {t('map.devices')}: {markers.length}
        </Typography>
        <Typography variant="caption" display="block" sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.8)' }}>
          {mapConfig.center.latitude.toFixed(4)}°, {mapConfig.center.longitude.toFixed(4)}°
        </Typography>
      </Paper>

      {/* Статистика устройств */}
      {markers.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 1000,
            p: 2,
            bgcolor: 'rgba(50, 50, 50, 0.9)', // Темный фон
            backdropFilter: 'blur(8px)',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              size="small" 
              label={`Online: ${markers.filter(m => m.device?.status === 'online').length}`}
              sx={{ 
                bgcolor: '#4caf50', 
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-label': { color: 'white' }
              }}
            />
            <Chip 
              size="small" 
              label={`Offline: ${markers.filter(m => m.device?.status === 'offline').length}`}
              sx={{ 
                bgcolor: '#757575', 
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-label': { color: 'white' }
              }}
            />
            {markers.filter(m => m.device?.status === 'error').length > 0 && (
              <Chip 
                size="small" 
                label={`Error: ${markers.filter(m => m.device?.status === 'error').length}`}
                sx={{ 
                  bgcolor: '#f44336', 
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-label': { color: 'white' }
                }}
              />
            )}
          </Box>
        </Paper>
      )}

      {/* Leaflet карта */}
      <MapContainer
        center={[mapConfig.center.latitude, mapConfig.center.longitude]}
        zoom={mapConfig.zoom}
        style={{ 
          height: '100%', 
          width: '100%',
          minHeight: '300px', // Минимальная высота для корректного отображения
          position: 'relative'
        }}
        attributionControl={true}
        zoomControl={false} // Отключаем стандартные контролы, используем свои
        whenReady={() => {
          // Принудительно обновляем размер карты после загрузки
          setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
          }, 100);
        }}
      >
        {/* Слой карты OpenStreetMap */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />

        {/* Контроллер карты */}
        <MapController 
          center={mapConfig.center} 
          zoom={mapConfig.zoom} 
          onZoomChange={setCurrentZoom}
        />

        {/* Обработчик кликов */}
        {interactive && <MapClickHandler onMapClick={onMapClick} />}

        {/* Маркеры устройств */}
        {markers.map((marker) => {
          const device = marker.device;
          const isSelected = selectedMarker?.id === marker.id;
          
          // Выбор иконки в зависимости от статуса
          let icon = DEVICE_ICONS.default;
          if (device?.status === 'online') icon = DEVICE_ICONS.online;
          else if (device?.status === 'offline') icon = DEVICE_ICONS.offline;
          else if (device?.status === 'error') icon = DEVICE_ICONS.error;

          return (
            <Marker
              key={marker.id}
              position={[marker.coordinates.latitude, marker.coordinates.longitude]}
              icon={icon}
              eventHandlers={{
                click: () => handleMarkerClick(marker),
              }}
            >
              <Popup>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {marker.name}
                  </Typography>
                  
                  {device && (
                    <>
                      <Typography variant="caption" display="block">
                        <strong>{t('devices.status')}:</strong> 
                        <Chip 
                          size="small" 
                          label={t(`devices.status.${device.status}`)}
                          color={
                            device.status === 'online' ? 'success' :
                            device.status === 'error' ? 'error' : 'default'
                          }
                          sx={{ ml: 0.5, height: 16, fontSize: '0.7rem' }}
                        />
                      </Typography>
                      
                      <Typography variant="caption" display="block">
                        <strong>{t('devices.type')}:</strong> {device.type || device.device_type}
                      </Typography>
                      
                      {device.full_address && (
                        <Typography variant="caption" display="block">
                          <strong>{t('devices.location')}:</strong> {device.full_address}
                        </Typography>
                      )}
                      
                      <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mt: 1 }}>
                        {marker.coordinates.latitude.toFixed(6)}, {marker.coordinates.longitude.toFixed(6)}
                      </Typography>
                    </>
                  )}
                  
                  {marker.tooltip && !device && (
                    <Typography variant="caption" color="text.secondary">
                      {marker.tooltip}
                    </Typography>
                  )}
                </Box>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Инструкция для пустой карты */}
      {markers.length === 0 && interactive && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        >
          <Alert 
            severity="info" 
            sx={{ 
              bgcolor: 'rgba(50, 50, 50, 0.95)', 
              color: 'white',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: '#2196f3' // Синяя иконка info
              }
            }}
          >
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
              {onMapClick ? t('map.clickToSelectLocation') : t('map.noDevicesOnMap')}
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
}; 